import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/i18n/context";
import { guidePathForGroup } from "@/content/guides/registry";

/**
 * The education hook the strategy review calls our strongest: most Slovenian
 * buyers do not know that asking prices sit above realised ones, and the gap is
 * simultaneously the demonstration of the product and the reason to buy it.
 *
 * Two things it deliberately does not do.
 *
 * It shows **no specific numbers as market data**. The figures below are framed
 * as an illustration of the question, not as a measured national average — we
 * have no defensible source for one, and the analysis itself refuses to
 * attribute the whole asking-vs-realised gap to overpricing (part is that the
 * compared properties differ, part is a wider catchment for listings than for
 * sales). Marketing must not claim what the product declines to.
 *
 * And it does not promise a discount. The link goes to the guide, which spends
 * a full section on why the gap is not all negotiating room.
 */
export function AskingVsRealized() {
  const { t, locale } = useLocale();
  const guideHref = guidePathForGroup("asking-vs-realized", locale);

  return (
    <section className="price-context-section">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="price-context-layout">
          <div>
            <p className="editorial-eyebrow">{t.askingVsRealized.eyebrow}</p>
            <h2 className="section-title mt-4">{t.askingVsRealized.title}</h2>
            <p className="mt-5 text-sm leading-relaxed text-muted-foreground">{t.askingVsRealized.answer}</p>
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{t.askingVsRealized.caveat}</p>
            {guideHref && (
              <Button asChild variant="link" className="mt-5 h-auto whitespace-normal justify-start px-0 text-left text-sm">
                <Link to={guideHref}>{t.askingVsRealized.cta}<ArrowRight className="ml-1 h-4 w-4 shrink-0" /></Link>
              </Button>
            )}
          </div>
          <div className="price-context-comparison">
            <p className="price-context-caption">{t.brand.illustration}</p>
            <div className="price-context-asking">
              <p>{t.askingVsRealized.askingLabel}</p><strong>{t.askingVsRealized.askingValue}</strong><span>{t.askingVsRealized.askingNote}</span>
            </div>
            <div className="price-context-sale">
              <p>{t.askingVsRealized.realizedLabel}</p><strong>{t.askingVsRealized.realizedValue}</strong><span>{t.askingVsRealized.realizedNote}</span>
            </div>
            <p className="price-context-note">{t.askingVsRealized.question}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
